import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { ApplicationCommentsStatus } from 'enums/onboarding/crm';

// components
import EllipsisTooltip from 'components/Tooltips/EllipsisTooltip';
import ApplicationCommentStatus from 'components/Additional/Statuses/ApplicationCommentStatus';
import { Text, Table, RequiredPropsForTableModel, TableColumnModel } from '@ui';

export interface FieldIssueModel {
  step: string;
  field: string;
  fieldId: string;
  status: ApplicationCommentsStatus;
  clientComment: string;
  reviewerComment: string;
}

interface IProps extends RequiredPropsForTableModel<FieldIssueModel> {
  onActionsClick?: (key: 'view', record: FieldIssueModel) => void;
}

const FieldIssuesTable = ({ onActionsClick, ...rest }: IProps) => {
  const { t } = useTranslation('onboarding');

  const tableColumns = useMemo<TableColumnModel[]>(() => {
    return [
      {
        width: 230,
        title: t(
          'intake_form.application_review_comments.field_issues.table.field',
        ),
        key: 'field',
        render: (record: FieldIssueModel) => (
          <EllipsisTooltip maxTextContainerWidth="200px" title={record.field}>
            {record.field}
          </EllipsisTooltip>
        ),
      },
      {
        width: 100,
        title: t(
          'intake_form.application_review_comments.field_issues.table.status',
        ),
        key: 'status',
        render: (record: FieldIssueModel) => (
          <ApplicationCommentStatus status={record.status} />
        ),
      },
      {
        width: 250,
        title: t(
          'intake_form.application_review_comments.field_issues.table.reviewer_comment',
        ),
        key: 'reviewerComment',
        render: (record: FieldIssueModel) => (
          <EllipsisTooltip
            maxTextContainerWidth="220px"
            title={record.reviewerComment}
          >
            {record.reviewerComment}
          </EllipsisTooltip>
        ),
      },
      {
        width: 150,
        title: t(
          'intake_form.application_review_comments.field_issues.table.your_comment',
        ),
        key: 'clientComment',
        render: (record: FieldIssueModel) => (
          <EllipsisTooltip
            maxTextContainerWidth="120px"
            title={record.clientComment}
          >
            {record.clientComment}
          </EllipsisTooltip>
        ),
      },
      {
        align: 'right',
        key: 'view',
        title: t(
          'intake_form.application_review_comments.field_issues.table.action',
        ),
        render: (record: FieldIssueModel) => (
          <Text
            variant="link1"
            onClick={() => onActionsClick && onActionsClick('view', record)}
          >
            {t(
              'intake_form.application_review_comments.field_issues.table.view_comment',
            )}
          </Text>
        ),
      },
    ];
  }, [onActionsClick]);

  return (
    <StyledTable {...rest} hidePagination size="small" columns={tableColumns} />
  );
};

const StyledTable = styled(Table)`
  .ant-table {
    background-color: transparent;

    .ant-table-thead > tr > th {
      background-color: transparent;
      border: none;
      padding: 0 ${({ theme }) => theme.paddingSm} !important;
    }

    .ant-table-tbody > tr > td {
      border: none;
      padding: 0 ${({ theme }) => theme.paddingSm} !important;
      &:first-child {
        padding-left: 0 !important;
      }
    }

    tr.ant-table-row: hover > td {
      background-color: transparent;
    }

    .ant-table-container table > thead > tr:first-child > *:first-child {
      padding-left: 0 !important;
    }
  }
`;

export default FieldIssuesTable;
